/*
 * custom-personalization-text-plus.js -- the font / colour / outline / icon
 * picker for the two "Customise Your Own Text" products (Oct 16 2026).
 * Markup: snippets/custom-personalization-text.liquid. Rendering engine:
 * cx-names-art.js (shared with the "Your Name on Your ..." products).
 *
 * Cart contract (read by custom-personalization-multi-add.js, mode
 * "textplus"):
 *   root.customPersonalizationBuildFile() -> Promise<{ file, properties }>
 *     file: the flattened transparent PNG, sent as "Custom Image".
 *     properties: "Your Text" / "Lettering" / "Fill" / "Outline" / "Icon"
 *     plus a hidden _cx_design JSON so the cart's Edit link can rebuild it.
 *     None of these keys may be "Custom Text" / "Font" / "Color" and no value
 *     may start with "http" -- Operations OS (customized-demand.ts) would
 *     otherwise re-typeset the order instead of printing this PNG.
 *   root.customPersonalizationBuildCanvas() -> Promise<canvas> (AR preview)
 *   root.customPersonalizationReset() / root.customPersonalizationPrefill()
 */
(function () {
  'use strict';

  var A = window.CXNamesArt;
  if (!A) return;

  var PRINT_MAX_SIDE = 2400; // >= 300 dpi at 8 inches
  var PREVIEW_MAX_SIDE = 900;
  var DEFAULT_FILL = 'White';

  function $(root, sel) {
    return root.querySelector(sel);
  }
  function $$(root, sel) {
    return Array.prototype.slice.call(root.querySelectorAll(sel));
  }
  function el(tag, cls, attrs) {
    var e = document.createElement(tag);
    if (cls) e.className = cls;
    if (attrs) {
      Object.keys(attrs).forEach(function (k) {
        if (k === 'text') e.textContent = attrs[k];
        else e.setAttribute(k, attrs[k]);
      });
    }
    return e;
  }

  function init(root) {
    var cfgEl = $(root, '[data-cxt-config]');
    if (!cfgEl) return;
    var cfg = JSON.parse(cfgEl.textContent);

    var input = $(root, '[data-custom-text-input]');
    var count = $(root, '[data-cxt-count]');
    var stage = $(root, '[data-cxt-stage]');
    var patchImg = $(root, '[data-cxt-patch]');
    var empty = $(root, '[data-cxt-empty]');
    var fontGrid = $(root, '[data-cxt-fonts]');
    var solidWrap = $(root, '[data-cxt-solids]');
    var gradWrap = $(root, '[data-cxt-gradients]');
    var fillLabel = $(root, '[data-cxt-fill-label]');
    var iconGrid = $(root, '[data-cxt-icons]');
    var outlineRadios = $$(root, '[data-cxt-outline]');
    var bgRadios = $$(root, '[data-cxt-bg]');
    var fontSelect = $(root, '[data-custom-font-select]');
    var colorSelect = $(root, '[data-custom-color-select]');

    var solids = A.SOLIDS.map(function (s) {
      return { type: 'solid', name: s[0], color: s[1] };
    });
    var gradients = A.GRADIENTS.map(function (g) {
      return { type: 'gradient', name: g[0], stops: g[1] };
    });
    function findFill(name) {
      var n = (name || '').replace(/ gradient$/i, '').toLowerCase();
      return solids.concat(gradients).filter(function (f) {
        return f.name.toLowerCase() === n;
      })[0];
    }
    function findFont(label) {
      var l = (label || '').toLowerCase();
      return cfg.fonts.filter(function (f) {
        return f.label.toLowerCase() === l;
      })[0];
    }

    var state = {
      font: cfg.fonts[0],
      fill: findFill(DEFAULT_FILL),
      outline: 'None',
      icon: null,
    };

    /* ---------------- hidden selects (multi-add.js reads these) ---------------- */

    cfg.fonts.forEach(function (f) {
      var o = el('option', '', { value: f.label, text: f.label });
      o.setAttribute('data-font-family', f.family);
      fontSelect.appendChild(o);
    });
    solids.concat(gradients).forEach(function (f) {
      var v = fillDisplayName(f);
      colorSelect.appendChild(el('option', '', { value: v, text: v }));
    });
    function syncSelects() {
      fontSelect.value = state.font.label;
      colorSelect.value = fillDisplayName(state.fill);
      fontSelect.dispatchEvent(new Event('change', { bubbles: true }));
      colorSelect.dispatchEvent(new Event('change', { bubbles: true }));
    }

    /* ---------------- font tiles ---------------- */

    var fontTiles = cfg.fonts.map(function (f) {
      var b = el('button', 'cx-names__font', { type: 'button', 'aria-pressed': 'false', 'aria-label': f.label });
      var sample = el('span', 'cx-names__font-sample');
      sample.style.fontFamily = "'" + f.family + "'";
      b.appendChild(sample);
      b.appendChild(el('span', 'cx-names__font-name', { text: f.label }));
      b.addEventListener('click', function () {
        state.font = f;
        syncAll();
      });
      fontGrid.appendChild(b);
      return { btn: b, sample: sample, font: f };
    });

    function fitFontTiles() {
      var text = input.value.trim() || 'Your Text';
      fontTiles.forEach(function (t) {
        t.sample.textContent = text;
        var avail = t.btn.clientWidth - 16;
        if (avail <= 0) return;
        t.sample.style.fontSize = '40px';
        var w = t.sample.getBoundingClientRect().width || 1;
        t.sample.style.fontSize = Math.max(11, Math.min(30, 40 * (avail / w))) + 'px';
      });
    }

    /* ---------------- colour swatches ---------------- */

    function fillDisplayName(f) {
      return f.type === 'gradient' ? f.name + ' gradient' : f.name;
    }
    function swatch(wrap, f, bg) {
      var b = el('button', 'cx-names__swatch', { type: 'button', 'aria-label': f.name, title: f.name, 'aria-pressed': 'false' });
      b.style.background = bg;
      if (f.name === 'White') b.classList.add('cx-names__swatch--light');
      b.addEventListener('click', function () {
        state.fill = f;
        syncAll();
      });
      b._cxFill = f;
      wrap.appendChild(b);
    }
    solids.forEach(function (f) {
      swatch(solidWrap, f, f.color);
    });
    gradients.forEach(function (f) {
      swatch(gradWrap, f, 'linear-gradient(90deg,' + f.stops.join(',') + ')');
    });

    /* ---------------- outline / icons / preview background ---------------- */

    outlineRadios.forEach(function (r) {
      r.addEventListener('change', function () {
        if (r.checked) {
          state.outline = r.value;
          syncAll();
        }
      });
    });
    function outlineHex() {
      if (state.outline === 'White') return '#ffffff';
      if (state.outline === 'Black') return '#111111';
      return null;
    }

    function iconButton(key, label) {
      var b = el('button', 'cx-names__icon', { type: 'button', 'aria-label': label, title: label, 'aria-pressed': 'false' });
      if (!key) {
        b.appendChild(el('span', 'cx-names__icon-none', { text: 'None' }));
      } else {
        var c = el('canvas', '', { width: '64', height: '64' });
        A.drawIcon(c.getContext('2d'), key, 6, 6, 52, '#1a1a1a');
        b.appendChild(c);
      }
      b.addEventListener('click', function () {
        state.icon = key;
        syncAll();
      });
      b._cxIcon = key;
      iconGrid.appendChild(b);
    }
    iconButton(null, 'No icon');
    cfg.icons.forEach(function (k) {
      if (A.ICONS[k]) iconButton(k, A.ICONS[k].label);
    });

    // Light text previews on dark, dark text on light; the customer can flip it.
    var bgTouched = false;
    bgRadios.forEach(function (r) {
      r.addEventListener('change', function () {
        if (r.checked) {
          bgTouched = true;
          stage.setAttribute('data-bg', r.value);
        }
      });
    });
    function autoBg() {
      if (bgTouched) return;
      var avg = state.fill.type === 'solid' ? state.fill.color : state.fill.stops[0];
      var dark = A.contrast(avg, '#2b2b2b') >= A.contrast(avg, '#f1eee9');
      if (state.outline === 'White') dark = true;
      if (state.outline === 'Black') dark = false;
      var v = dark ? 'dark' : 'light';
      stage.setAttribute('data-bg', v);
      bgRadios.forEach(function (r) {
        r.checked = r.value === v;
      });
    }

    /* ---------------- render ---------------- */

    function composeAt(maxSide) {
      return A.compose({
        text: input.value.trim(),
        fontFamily: state.font.family,
        fill: state.fill,
        outline: outlineHex(),
        icon: state.icon,
        maxSide: maxSide,
      }).then(function (r) {
        return r.canvas;
      });
    }

    var renderSeq = 0;
    function renderPreview() {
      var text = input.value.trim();
      if (!text && !state.icon) {
        renderSeq++; // drop any render still in flight
        patchImg.hidden = true;
        empty.hidden = false;
        return;
      }
      var seq = ++renderSeq;
      composeAt(PREVIEW_MAX_SIDE).then(function (canvas) {
        if (seq !== renderSeq) return;
        patchImg.src = canvas.toDataURL('image/png');
        patchImg.hidden = false;
        empty.hidden = true;
      });
    }

    var pending = null;
    function schedule() {
      if (pending) cancelAnimationFrame(pending);
      pending = requestAnimationFrame(function () {
        pending = null;
        renderPreview();
      });
    }

    function syncAll() {
      fontTiles.forEach(function (t) {
        t.btn.setAttribute('aria-pressed', t.font === state.font ? 'true' : 'false');
      });
      $$(root, '.cx-names__swatch').forEach(function (b) {
        b.setAttribute('aria-pressed', b._cxFill === state.fill ? 'true' : 'false');
      });
      if (fillLabel) fillLabel.textContent = fillDisplayName(state.fill);
      outlineRadios.forEach(function (r) {
        r.checked = r.value === state.outline;
      });
      $$(iconGrid, '.cx-names__icon').forEach(function (b) {
        b.setAttribute('aria-pressed', b._cxIcon === state.icon ? 'true' : 'false');
      });
      autoBg();
      syncSelects();
      schedule();
    }

    input.addEventListener('input', function () {
      if (count) count.textContent = input.value.length + '/' + input.maxLength;
      fitFontTiles();
      schedule();
    });
    window.addEventListener('resize', fitFontTiles);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(fitFontTiles);

    /* ---------------- hooks for multi-add.js / AR ---------------- */

    function lineProperties() {
      var props = {
        'Your Text': input.value.trim(),
        Lettering: state.font.label,
        Fill: fillDisplayName(state.fill),
      };
      if (state.outline !== 'None') props.Outline = state.outline;
      if (state.icon) props.Icon = A.ICONS[state.icon].label;
      props._cx_design = JSON.stringify({
        t: input.value.trim(),
        f: state.font.label,
        c: fillDisplayName(state.fill),
        o: state.outline,
        i: state.icon,
      });
      return props;
    }

    root.customPersonalizationBuildFile = function () {
      return composeAt(PRINT_MAX_SIDE).then(function (canvas) {
        return new Promise(function (resolve, reject) {
          canvas.toBlob(function (blob) {
            if (!blob) return reject(new Error('Could not prepare your design. Please try again.'));
            resolve({ file: new File([blob], 'text-patch.png', { type: 'image/png' }), properties: lineProperties() });
          }, 'image/png');
        });
      });
    };

    root.customPersonalizationBuildCanvas = function () {
      if (!input.value.trim()) return Promise.reject(new Error('Type your text first, then tap this again.'));
      return composeAt(1200);
    };

    function applyDesign(d) {
      if (!d) return;
      if (typeof d.t === 'string') input.value = d.t.slice(0, input.maxLength);
      state.font = findFont(d.f) || state.font;
      state.fill = findFill(d.c) || state.fill;
      state.outline = d.o === 'White' || d.o === 'Black' ? d.o : 'None';
      state.icon = d.i && A.ICONS[d.i] ? d.i : null;
      input.dispatchEvent(new Event('input', { bubbles: true }));
      syncAll();
    }

    root.customPersonalizationReset = function () {
      input.value = '';
      state.icon = null;
      input.dispatchEvent(new Event('input', { bubbles: true }));
      syncAll();
    };

    // Edit from cart. New lines carry the Custom Image URL (edit_image) --
    // look the line up in the cart and rebuild from its hidden _cx_design.
    // Lines added before Oct 16 carry plain text/font/color.
    root.customPersonalizationPrefill = function (data) {
      if (data.imageUrl) {
        fetch((window.routes && window.routes.cart_url ? window.routes.cart_url : '/cart') + '.js', {
          headers: { Accept: 'application/json' },
        })
          .then(function (r) {
            return r.json();
          })
          .then(function (cart) {
            var line = (cart.items || []).filter(function (it) {
              return it.properties && it.properties['Custom Image'] === data.imageUrl && it.properties._cx_design;
            })[0];
            if (line) applyDesign(JSON.parse(line.properties._cx_design));
          })
          .catch(function () {});
        return;
      }
      applyDesign({ t: data.text || '', f: data.font, c: data.color, o: 'None', i: null });
    };

    syncAll();
    fitFontTiles();
  }

  document.querySelectorAll('[data-custom-personalization][data-mode="textplus"]').forEach(init);
})();
